import type { GeneralChatMessage } from '../../types'
import type { CardView, ChatCard, ThreadItem } from './cardResults'
import { loadKeyOf } from './loadKey'
import { amazonRowsOf, isDrawnCard } from './cardResults'

type TurnItem =
    | { kind: 'message'; message: GeneralChatMessage }
    // `question`: the user's message the turn answers (a table opens on the filter it asks for), absent before the first one.
    | { kind: 'turn'; id: string; cards: ChatCard[]; replies: GeneralChatMessage[]; asOf: string; question?: string }

/**
 * Groups the thread into turns: from one user message to the next, the cards and the agent's replies that came between them form one
 * answer block. A reply with no cards in its turn stays a plain message.
 */
export function turnsOf(thread: ThreadItem[]): TurnItem[] {
    const out: TurnItem[] = []
    let open: { cards: ChatCard[]; replies: GeneralChatMessage[]; asOf: string; id: string; question?: string } | null = null
    let question: string | undefined
    const close = () => {
        if (open) out.push({ kind: 'turn', ...open })
        open = null
    }
    for (const item of thread) {
        if (item.kind === 'card') {
            // A drawer's own write is shown by its drawer and the rows it updated in place, never as a row of its own (v17).
            if (!isDrawnCard(item.card)) continue
            if (!open) open = { cards: [], replies: [], asOf: item.card.createdAt, id: item.card.id, question }
            open.cards.push(item.card)
            open.asOf = item.card.createdAt
            continue
        }
        const { message } = item
        if (message.message_type === 'user') {
            close()
            question = message.content ?? undefined
            out.push({ kind: 'message', message })
        } else if (open) {
            open.replies.push(message)
        } else {
            out.push({ kind: 'message', message })
        }
    }
    close()
    return out
}

type LoadRows = { load_id?: string; load_number?: string | null; load_numbers?: string[] }

/** The card's rows of load views, with those a later read (the newer state) already answers for left out. */
function withoutAnswered(view: CardView, answered: Set<string>): CardView | null {
    const fresh = <T extends LoadRows>(rows: T[]): T[] =>
        rows.filter((row) => {
            const key = loadKeyOf(row)
            return key === undefined || !answered.has(key)
        })
    if (view.view === 'loads') {
        const rows = fresh(view.rows)
        return rows.length ? { ...view, rows } : null
    }
    if (view.view === 'invoices') {
        const rows = fresh(view.rows)
        return rows.length ? { ...view, rows } : null
    }
    if (view.view === 'readiness') {
        const { readiness } = view
        const invoicedRows = fresh(readiness.invoiced.rows)
        const dropped = readiness.invoiced.rows.length - invoicedRows.length
        // A dropped invoice's amount is no longer the group's sum, so the group shows its count without a total.
        const invoiced = dropped ? { count: Math.max(0, readiness.invoiced.count - dropped), amount_usd: null, rows: invoicedRows } : readiness.invoiced
        const next = {
            ...readiness,
            ready: fresh(readiness.ready),
            needs_documents: fresh(readiness.needs_documents),
            not_delivered: fresh(readiness.not_delivered),
            invoiced,
        }
        return next.ready.length || next.needs_documents.length || next.not_delivered.length || next.invoiced.count || next.ambiguous.length
            ? { ...view, readiness: next }
            : null
    }
    return view
}

/** The load keys a read card's rows answer for; a write card, or a card with no load rows, answers for nothing here. */
function readKeysOf(view: CardView): string[] {
    if (view.view === 'loads' || view.view === 'invoices') return view.rows.map(loadKeyOf).filter((k): k is string => k !== undefined)
    if (view.view === 'readiness') {
        const { ready, needs_documents: needs, not_delivered: away, invoiced } = view.readiness
        return [...ready, ...needs, ...away, ...invoiced.rows].map(loadKeyOf).filter((k): k is string => k !== undefined)
    }
    return []
}

const isRead = (card: ChatCard): boolean => card.view.view !== 'writes'

/** An invoice write answers for the load it invoiced; other writes keep their own rows. */
const isInvoiceWrite = (card: ChatCard): boolean => card.view.view === 'writes' && card.view.card === 'tms_invoices.create'

/**
 * One turn's cards with each load once, the newest state winning. Walks from the newest card back. A read's rows are left out when a
 * newer card already answers for their load, and a card with nothing left is dropped. A write's row is left out when a newer write
 * answers for its load or a newer read shows it. An invoice that was written is the exception to the read rule: the read row turns into
 * the written state in place (design §7), so an invoiced load the turn's reads show draws no row of its own. Every other write (a
 * refusal, a status change) answers for its load, so the older reads of that load are left out and its row carries the outcome.
 */
/**
 * A load a block's readiness card already lists is not listed again by a loads card of the same block: the readiness card is the one row
 * for it, in its group, so the block shows each load once. A loads card left with no row is dropped.
 */
export function withoutCoveredLoads(input: ChatCard[]): ChatCard[] {
    // Cards are oldest first: a loads card is trimmed only by a readiness card that came after it (the newer read wins).
    const laterCovered = (index: number): Set<string> => {
        const covered = new Set<string>()
        for (const card of input.slice(index + 1)) {
            if (card.view.view !== 'readiness') continue
            const { readiness } = card.view
            const rows = [...readiness.ready, ...readiness.needs_documents, ...readiness.invoiced.rows, ...amazonRowsOf(readiness.billed_through_amazon)]
            rows.forEach((row) => row.load_numbers.forEach((number) => covered.add(number)))
            readiness.not_delivered.forEach((row) => row.load_number && covered.add(row.load_number))
        }
        return covered
    }
    return input.flatMap((card, index) => {
        if (card.view.view !== 'loads') return [card]
        const covered = laterCovered(index)
        const rows = card.view.rows.filter((row) => !(row.load_number && covered.has(row.load_number)))
        if (rows.length === card.view.rows.length) return [card]
        return rows.length ? [{ ...card, view: { ...card.view, rows } }] : []
    })
}

export function mergeTurnCards(input: ChatCard[]): ChatCard[] {
    const cards = withoutCoveredLoads(input)
    const readKeys = new Set(cards.filter(isRead).flatMap((card) => readKeysOf(card.view)))
    const answered = new Set<string>()
    const writtenAfter = new Set<string>()
    const kept: Array<ChatCard | null> = cards.map(() => null)
    for (let i = cards.length - 1; i >= 0; i--) {
        const card = cards[i]
        if (isRead(card)) {
            const view = withoutAnswered(card.view, answered)
            readKeysOf(card.view).forEach((k) => answered.add(k))
            if (view) kept[i] = { ...card, view }
            continue
        }
        if (card.view.view !== 'writes') continue
        const invoice = isInvoiceWrite(card)
        const rows = card.view.rows.filter((row) => {
            const key = loadKeyOf(row)
            if (key === undefined) return true
            if (writtenAfter.has(key)) return false
            if (invoice && row.outcome === 'updated') return !readKeys.has(key)
            return !answered.has(key)
        })
        card.view.rows.forEach((row) => {
            const key = loadKeyOf(row)
            if (key === undefined) return
            writtenAfter.add(key)
            if (!invoice || row.outcome !== 'updated') answered.add(key)
        })
        if (rows.length) kept[i] = { ...card, view: { ...card.view, rows } }
    }
    return kept.filter((card): card is ChatCard => card !== null)
}

/**
 * What each load row of a block shows, by its key: a row's status, its rate, its invoice, its readiness group. Two blocks differ on
 * a load when a key's state differs; a write's outcome is part of the state too.
 */
export function stateOfCard(cards: ChatCard[]): Map<string, string> {
    const states = new Map<string, string>()
    const put = (key: string | undefined, state: string) => {
        if (key !== undefined) states.set(key, state)
    }
    for (const { view } of cards) {
        if (view.view === 'loads') view.rows.forEach((row) => put(loadKeyOf(row), `${row.status}|${row.broker_rate_usd ?? ''}`))
        else if (view.view === 'invoices')
            view.rows.forEach((row) => put(loadKeyOf(row), `${row.status_group}|${row.invoice_number ?? ''}|${row.readiness ?? ''}`))
        else if (view.view === 'writes')
            // A truck write shows on the load's row through its stored assign, so it does not change the row's state here.
            view.rows.forEach((row) => {
                if (view.card !== 'tms_loads.assign' || row.outcome !== 'updated') put(loadKeyOf(row), `write:${row.outcome}`)
            })
        else if (view.view === 'readiness') {
            const { ready, needs_documents: needs, not_delivered: away, invoiced } = view.readiness
            ready.forEach((row) => put(loadKeyOf(row), 'ready'))
            needs.forEach((row) => put(loadKeyOf(row), 'needs_documents'))
            invoiced.rows.forEach((row) => put(loadKeyOf(row), `invoiced|${row.invoice_number ?? ''}`))
            away.forEach((row) => put(loadKeyOf(row), `away|${row.status}`))
        }
    }
    return states
}

/**
 * For each block (oldest first), the keys whose state a newer block shows differently: those rows are dimmed in this older block.
 * Walks from the newest block back, so each key is compared with the nearest newer block that shows it.
 */
export function updatedBelowOf(blocks: Array<Map<string, string>>): Array<Set<string>> {
    const result: Array<Set<string>> = blocks.map(() => new Set<string>())
    const later = new Map<string, string>()
    for (let i = blocks.length - 1; i >= 0; i--) {
        blocks[i].forEach((state, key) => {
            const next = later.get(key)
            if (next !== undefined && next !== state) result[i].add(key)
        })
        blocks[i].forEach((state, key) => later.set(key, state))
    }
    return result
}
